import React, { useState, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { useSearchParams } from 'react-router-dom';
import { backendService } from '../services/backendService';
import { SuccessIcon, ImageUploadIcon, RetryIcon, CheckIcon } from './Icons';
import './PhotoUpload.css';

const CROP_ASPECT = 1; // 定义剪裁比例为1:1 (宽:高)

interface PhotoUploadProps {}

// 辅助函数：创建图像并返回Promise
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

// 压缩配置选项
interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxFileSizeKB?: number;
}

// 裁剪并压缩图像的函数
const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  outputWidth = 1024,
  outputHeight = 1365,
  compressionOptions: CompressionOptions = {}
): Promise<{
  dataUrl: string;
  stats: {
    originalSize: number;
    compressedSize: number;
    quality: number;
    dimensions: string;
  }
}> => {
  const {
    maxWidth = 1024,
    maxHeight = 1365,
    quality = 0.8,
    maxFileSizeKB = 2048 // 默认最大文件大小为2MB
  } = compressionOptions;

  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  // 记录原始文件大小
  const originalSize = getBase64Size(imageSrc);

  // 计算最终尺寸，确保不超过最大限制
  let finalWidth = outputWidth;
  let finalHeight = outputHeight;
  
  if (finalWidth > maxWidth || finalHeight > maxHeight) {
    const aspectRatio = finalWidth / finalHeight;
    if (finalWidth > maxWidth) {
      finalWidth = maxWidth;
      finalHeight = finalWidth / aspectRatio;
    }
    if (finalHeight > maxHeight) {
      finalHeight = maxHeight;
      finalWidth = finalHeight * aspectRatio;
    }
  }

  // 设置画布尺寸
  canvas.width = finalWidth;
  canvas.height = finalHeight;

  // 启用图像平滑处理以获得更好的质量
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // 绘制裁剪后的图像
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    finalWidth,
    finalHeight
  );

  // 尝试不同的质量级别以达到目标文件大小
  let currentQuality = quality;
  let compressedImage = canvas.toDataURL('image/jpeg', currentQuality);
  
  // 如果文件太大，逐步降低质量
  while (getBase64Size(compressedImage) > maxFileSizeKB && currentQuality > 0.1) {
    currentQuality -= 0.1;
    compressedImage = canvas.toDataURL('image/jpeg', currentQuality);
  }

  const compressedSize = getBase64Size(compressedImage);
  const stats = {
    originalSize,
    compressedSize,
    quality: currentQuality,
    dimensions: `${finalWidth}x${finalHeight}`
  };

  // console.log(`图片压缩完成: 尺寸 ${finalWidth}x${finalHeight}, 质量 ${currentQuality.toFixed(1)}, 原始大小 ${originalSize}KB, 压缩后 ${compressedSize}KB`);
  
  return {
    dataUrl: compressedImage,
    stats
  };
};

// 计算Base64图像的大小（KB）
const getBase64Size = (base64String: string): number => {
  const base64Data = base64String.split(',')[1];
  const byteLength = (base64Data.length * 3) / 4;
  return Math.round(byteLength / 1024);
};

const PhotoUpload: React.FC<PhotoUploadProps> = () => {
  // const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session'); // 获取会话ID
  const aspectParam = searchParams.get('aspect'); // 获取aspect参数
  
  // 根据URL参数确定裁剪比例
  const getCropAspect = () => {
    if (aspectParam) {
      const aspectRatio = parseFloat(aspectParam);
      if (!isNaN(aspectRatio) && aspectRatio > 0) {
        console.log('PhotoUpload: 使用URL参数中的aspect比例:', aspectRatio);
        return aspectRatio;
      }
    }
    console.log('PhotoUpload: 使用默认正方形比例 (1:1)');
    return CROP_ASPECT; // 默认使用原有的1:1比例
  };

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  
  // react-easy-crop 相关状态
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // 记录URL参数变化
  // React.useEffect(() => {
  //   console.log('PhotoUpload: sessionId =', sessionId);
  //   console.log('PhotoUpload: aspectParam =', aspectParam);
  //   console.log('PhotoUpload: 当前裁剪比例 =', getCropAspect());
  // }, [sessionId, aspectParam]);
  
  // 处理文件选择
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    
    if (!file) {
      return;
    }
    
    // 扩展支持的文件类型检查
    if (!file.type.startsWith('image/')) {
      setError('请上传图片文件');
      return;
    }
    
    // 增加文件大小限制到20MB
    if (file.size > 20 * 1024 * 1024) {
      setError('图片大小不能超过20MB');
      return;
    }
    
    setIsLoading(true);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target?.result as string;
      if (imageData) {
        // 设置预览并进入剪裁模式
        setImagePreview(imageData);
        setIsCropping(true);
        setIsLoading(false);
        // 重置裁剪状态
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      }
    };
    
    reader.onerror = () => {
      setError('读取文件失败');
      setIsLoading(false);
    };
    
    reader.readAsDataURL(file);
  };

  // 处理裁剪区域变化
  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  // 完成剪裁和压缩
  const handleCropComplete = async () => {
    if (!imagePreview || !croppedAreaPixels) return;
    
    try {
      setIsLoading(true);
      
      // 根据aspect参数确定输出尺寸
      const currentAspect = getCropAspect();
      let outputWidth = 1200;
      let outputHeight = 1200;
      let maxWidth = 1200;
      let maxHeight = 1200;
      
      if (currentAspect !== 1) {
        // 非正方形比例，根据aspect调整尺寸
        if (currentAspect < 1) {
          // 高度大于宽度 (如3:4比例，aspect=0.75)
          outputWidth = 900;  // 基础宽度
          outputHeight = Math.round(outputWidth / currentAspect); // 根据比例计算高度
          maxWidth = 900;
          maxHeight = Math.round(maxWidth / currentAspect);
        } else {
          // 宽度大于高度
          outputHeight = 900;
          outputWidth = Math.round(outputHeight * currentAspect);
          maxHeight = 900;
          maxWidth = Math.round(maxHeight * currentAspect);
        }
        console.log(`PhotoUpload: 使用自定义比例 ${currentAspect}, 输出尺寸: ${outputWidth}x${outputHeight}`);
      } else {
        console.log('PhotoUpload: 使用正方形比例，输出尺寸: 1200x1200');
      }
      
      // 压缩配置 - 针对移动端上传优化
      const compressionOptions: CompressionOptions = {
        maxWidth,       // 根据比例设置最大宽度
        maxHeight,      // 根据比例设置最大高度
        quality: 0.85,      // 初始质量
        maxFileSizeKB: 600  // 最大文件大小600KB
      };
      
      const result = await getCroppedImg(
        imagePreview, 
        croppedAreaPixels, 
        outputWidth,  // 根据比例设置输出宽度
        outputHeight, // 根据比例设置输出高度
        compressionOptions
      );
      
      setImagePreview(result.dataUrl);
      setIsCropping(false);
      setIsLoading(false);
    } catch (e) {
      console.error("裁剪图片失败:", e);
      setError('剪裁图片失败');
      setIsCropping(false);
      setIsLoading(false);
    }
  };
  
  // 取消剪裁
  const handleCropCancel = () => {
    setIsCropping(false);
    setImagePreview(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };
  
  // 处理点击上传按钮
  const handleClickUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // 处理确认使用按钮
  const handleConfirm = async () => {
    if (imagePreview) {
      try {
        setIsLoading(true);
        setError(null);
        
        // 如果有sessionId，发送到服务器
        if (sessionId) {
          await backendService.uploadPhotoToSession(sessionId, imagePreview);
          // console.log('照片已上传到会话:', sessionId);
          
          // 显示上传成功状态
          setUploadSuccess(true);
        }
      } catch (error) {
        console.error('上传失败:', error || '未知错误');
        setError('上传失败，请重试');
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  // 处理重新选择
  const handleReselect = () => {
    setImagePreview(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 处理重置裁剪
  const handleResetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };
  
  return (
    <div className="photo-upload-container">
      <div className="photo-upload-content">
        {/* 上传成功全屏页面 */}
        {uploadSuccess ? (
          <div className="upload-success-page">
            <div className="success-content">
              <div className="success-icon-large">
                <SuccessIcon width="64" height="64" />
              </div>
              <h2 className="success-title">上传成功</h2>
              <p className="success-description">照片上传成功，请回到大屏幕继续制作</p>
            </div>
          </div>
        ) : (
          <>
            {/* 会话状态显示 */}
            {!sessionId && (
              <div className="session-indicator disconnected">
                <span className="session-icon">⚠️</span>
                <span className="session-text">未连接到主机</span>
                <span className="session-hint">请从主机扫码进入</span>
              </div>
            )}
            {isCropping ? (
              <div className="crop-fullscreen-container">
                <div className="crop-header">
                  <button className="crop-reset-button" onClick={handleResetCrop}>
                    还原
                  </button>
                </div>
                <div className="crop-image-wrapper">
                  {imagePreview && (
                    <Cropper
                      image={imagePreview}
                      crop={crop}
                      zoom={zoom}
                      aspect={getCropAspect()}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      showGrid={true}
                      style={{
                        containerStyle: {
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#000',
                        },
                      }}
                    />
                  )}
                </div>
                <div className="crop-bottom-actions">
                  <button 
                    className="crop-action-button crop-cancel-button"
                    onClick={handleCropCancel}
                  >
                    取消
                  </button>
                  <button 
                    className="crop-action-button crop-confirm-button"
                    onClick={handleCropComplete}
                    disabled={isLoading}
                  >
                    {isLoading ? '压缩处理中...' : '完成'}
                  </button>
                </div>
                
                {/* 在裁剪模式下也显示错误信息 */}
                {error && (
                  <div className="error-message error-message--crop">
                    <p>{error}</p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="upload-area">
                  {!imagePreview ? (
                    <>
                      <div className="upload-placeholder" onClick={handleClickUpload}>
                        <div className="upload-icon">
                          <ImageUploadIcon width="60" height="60" />
                        </div>
                        <p className="upload-hint">点击此处选择一张照片</p>
                        <input 
                          type="file" 
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                      </div>
                      {isLoading && (
                        <div className="loading-indicator">
                          <div className="loading-spinner"></div>
                          <p>图片处理中...</p>
                        </div>
                      )}
                      {error && (
                        <div className="error-message">
                          <p>{error}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="image-preview-section">
                      <div className="image-preview-container">
                        <img 
                          src={imagePreview} 
                          alt="预览图"
                          className="image-preview"
                          style={{
                            aspectRatio: getCropAspect()
                          }}
                        />
                      </div>
                      
                      <div className="image-actions">
                        <button 
                          className="action-button reselect-button"
                          onClick={handleReselect}
                        >
                          <RetryIcon width="20" height="20" />
                          重新选择
                        </button>
                        <button 
                          className="action-button confirm-button"
                          onClick={handleConfirm}
                          disabled={isLoading}
                        >
                          <CheckIcon width="20" height="20" />
                          {isLoading ? '上传中...' : '使用照片'}
                        </button>
                      </div>
                      
                      {/* 在预览区域也显示错误信息 */}
                      {error && (
                        <div className="error-message">
                          <p>{error}</p>
                        </div>
                      )}
                      
                      {/* 显示加载状态 */}
                      {isLoading && (
                        <div className="loading-indicator">
                          <div className="loading-spinner"></div>
                          <p>上传中...</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="upload-instructions">
                  <p className="instructions-text">
                    请确保图片清晰、光线充足，并且人物的头部和身体完整出现在画面中，避免使用过于复杂的背景。
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PhotoUpload;